import { forwardRef, type ComponentType, type SVGAttributes } from "react";

import { Box, CloseIcon, IconButton, TextWithTooltip } from "@hightouch/htui";

export type ComposerContextChip = Readonly<{
  id: string;
  label: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  removeLabel: string;
  onRemove: () => void;
  /** A preset request the next message sends before the typed text, so the composer shows the chip instead of the request. */
  prompt?: string;
}>;
function ComposerContextChipContent({
  label,
  icon,
  removeLabel,
  onRemove,
}: ComposerContextChip) {
  return (
    <Box
      data-context-chip
      data-group
      display="grid"
      gridTemplateColumns="auto minmax(0, 1fr)"
      alignItems="center"
      gap={1}
      flexShrink={0}
      width="fit-content"
      maxWidth="min(262px, 100%)"
      color="text.accent"
      // Reveal on keyboard focus without replacing IconButton's focus styles.
      sx={{ "&:has(:focus-visible) > button": { opacity: 1 } }}
    >
      <IconButton
        icon={CloseIcon}
        size="sm"
        variant="primary"
        type="button"
        data-peer
        gridArea="1 / 1"
        boxSize={4}
        borderRadius="xs"
        color="text.accent"
        bg="transparent"
        boxShadow="none"
        aria-label={removeLabel}
        onClick={onRemove}
        opacity={0}
        _hover={{ bg: "transparent" }}
        _active={{ bg: "transparent", transform: "none" }}
        _groupHover={{ opacity: 1 }}
      />
      <Box
        as={icon}
        aria-hidden
        gridArea="1 / 1"
        boxSize={4}
        pointerEvents="none"
        _groupHover={{ opacity: 0 }}
        _peerFocusVisible={{ opacity: 0 }}
      />
      <TextWithTooltip
        size="md"
        fontWeight="medium"
        color="text.accent"
        placement="top-start"
      >
        {label}
      </TextWithTooltip>
    </Box>
  );
}

type RowProps = {
  chips: readonly ComposerContextChip[];
  onRemove: (chip: ComposerContextChip) => void;
  /** "overlay" floats over the start of a textarea; "row" takes its own line in the layout. */
  layout?: "overlay" | "row";
};

export const ComposerContextChipRow = forwardRef<HTMLDivElement, RowProps>(
  function ComposerContextChipRow(
    { chips, onRemove, layout = "overlay" },
    ref,
  ) {
    return (
      <Box
        ref={ref}
        data-context-chip-row
        {...(layout === "overlay"
          ? { position: "absolute", insetInlineStart: 0, top: 0, zIndex: 1 }
          : {})}
        display="flex"
        flexWrap="wrap"
        width="fit-content"
        maxWidth="100%"
        gap={2}
      >
        {chips.map((chip) => (
          <ComposerContextChipContent
            key={chip.id}
            {...chip}
            onRemove={() => onRemove(chip)}
          />
        ))}
      </Box>
    );
  },
);
